import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Label,
  Input,
  Button,
  Table,
  Spinner,
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { abrirEtiquetasRollos } from "../../../../utils/etiquetaRolloTela";

// Salida y devolución de tela de un corte (antes se hacía en Órdenes de Corte, que ya no se
// usan a mano). Se escanea el QR de cada rollo; el back valida y hace todo en una sola operación.
//  - Salida: liga los rollos al corte y a su orden de corte automática + acumulados.
//  - Devolución: rollo completo vuelve libre; parcial crea un rollo nuevo con lo devuelto (QR).
const URL_TELAS_ORDENES_CORTE = process.env.REACT_APP_URL_TELAS_ORDENES_CORTE;
const URL_LOGS = process.env.REACT_APP_URL_LOGS;

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n || 0);

function SalidaTelaCorte({ isOpen, toggle, corte, user }) {
  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("NA");
  const [scan, setScan] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [rollos, setRollos] = useState([]);
  const [entregados, setEntregados] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [modo, setModo] = useState("salida"); // "salida" | "devolucion"
  const [devs, setDevs] = useState([]); // rollos a devolver: {...rollo, regresa}
  const scanRef = useRef(null);
  const enfocar = () =>
    setTimeout(() => scanRef.current && scanRef.current.focus(), 0);

  function cargarEntregados() {
    axios
      .get(`${URL_TELAS_ORDENES_CORTE}/corte/${corte.id}`, { headers })
      .then((r) => setEntregados(r.data || []))
      .catch(() => setEntregados([]));
  }

  useEffect(() => {
    if (!isOpen || !corte) return;
    setFecha(moment().format("YYYY-MM-DD"));
    setObservaciones("NA");
    setScan("");
    setRollos([]);
    setDevs([]);
    setModo("salida");
    cargarEntregados();
    setTimeout(() => scanRef.current && scanRef.current.focus(), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, corte && corte.id]);

  // Devolución: agrega un rollo entregado (escaneado o desde la lista de entregados)
  function agregarDevolucion(r) {
    if (devs.some((x) => x._id === r._id)) {
      Swal.fire({
        icon: "warning",
        title: "Ese rollo ya está en la devolución",
        timer: 1500,
        showConfirmButton: false,
      });
      return;
    }
    setDevs((prev) => [...prev, { ...r, regresa: r.cantidad }]);
  }

  async function escanear() {
    if (modo === "salida") return agregarRollo();
    const id = scan.trim();
    if (!id) return;
    setBuscando(true);
    try {
      const { data } = await axios.get(
        `${URL_TELAS_ORDENES_CORTE}/rolloDevolucion/${corte.id}/${id}`,
        { headers },
      );
      agregarDevolucion(data);
    } catch (e) {
      const d = e.response && e.response.data;
      Swal.fire({
        icon: "error",
        title: "No se puede devolver",
        text: (d && d.message) || "Rollo no encontrado",
        timer: 2500,
      });
    }
    setBuscando(false);
    setScan("");
    enfocar();
  }

  async function guardarDevolucion() {
    if (!devs.length) {
      Swal.fire({ icon: "warning", title: "Escanea al menos un rollo" });
      return;
    }
    const malo = devs.find(
      (r) => !(Number(r.regresa) > 0) || Number(r.regresa) > Number(r.cantidad),
    );
    if (malo) {
      Swal.fire({
        icon: "warning",
        title: `Revisa lo que regresa del rollo ${malo.captura || malo._id}`,
        text: `Debe ser mayor a 0 y máximo ${malo.cantidad}`,
      });
      return;
    }
    const parciales = devs.filter(
      (r) => Number(r.regresa) < Number(r.cantidad),
    ).length;
    const ok = await Swal.fire({
      title: `¿Devolver ${devs.length} rollo(s) al almacén?`,
      html: parciales
        ? `${parciales} parcial(es): se creará un rollo nuevo con lo que regresa y se imprimirá su QR.`
        : "",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, devolver",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    try {
      const { data } = await axios.post(
        `${URL_TELAS_ORDENES_CORTE}/devolucionCorte`,
        {
          cortes: corte.id,
          fecha,
          rollos: devs.map((r) => ({ id: r._id, cantidad: Number(r.regresa) })),
        },
        { headers },
      );
      axios
        .post(
          URL_LOGS,
          {
            tipo: "Devolución de Tela de Corte",
            detalle: `Corte ${corte.numero}: ${devs.length} rollos`,
            user: user.id,
          },
          { headers },
        )
        .catch(() => {});
      if ((data.nuevosRollos || []).length) {
        await Swal.fire({
          icon: "success",
          title: "Devolución registrada",
          text: `Se crearon ${data.nuevosRollos.length} rollo(s) nuevo(s) con lo devuelto. Se abren sus etiquetas para imprimir.`,
        });
        abrirEtiquetasRollos(data.nuevosRollos);
      } else {
        await Swal.fire({
          icon: "success",
          title: "Devolución registrada",
          text: `${data.completos} rollo(s) regresaron completos al almacén.`,
        });
      }
      setDevs([]);
      cargarEntregados();
    } catch (e) {
      const d = e.response && e.response.data;
      Swal.fire({
        icon: "error",
        title: "No se pudo registrar",
        text: (d && d.message) || "Error al guardar",
      });
    }
    setGuardando(false);
  }

  async function agregarRollo() {
    const id = scan.trim();
    if (!id) return;
    if (rollos.some((r) => r._id === id)) {
      Swal.fire({
        icon: "warning",
        title: "Ese rollo ya está en la lista",
        timer: 1500,
        showConfirmButton: false,
      });
      setScan("");
      return;
    }
    setBuscando(true);
    try {
      const { data } = await axios.get(
        `${URL_TELAS_ORDENES_CORTE}/rollo/${id}`,
        { headers },
      );
      setRollos((prev) => [...prev, { ...data, bies: "No" }]);
    } catch (e) {
      const d = e.response && e.response.data;
      Swal.fire({
        icon: "error",
        title: "No se puede usar",
        text: (d && d.message) || "Rollo no encontrado",
        timer: 2500,
      });
    }
    setBuscando(false);
    setScan("");
    setTimeout(() => scanRef.current && scanRef.current.focus(), 0);
  }

  const totalUnidad = (u) =>
    rollos
      .filter((r) => r.unidad === u)
      .reduce((t, r) => t + Number(r.cantidad || 0), 0);

  async function guardar() {
    if (!rollos.length) {
      Swal.fire({ icon: "warning", title: "Escanea al menos un rollo" });
      return;
    }
    const ok = await Swal.fire({
      title: `¿Dar salida a ${rollos.length} rollo(s)?`,
      text: `Corte ${corte.numero}: ${fmt(totalUnidad("Metros"))} mts / ${fmt(totalUnidad("Kilos"))} kg`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, dar salida",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    try {
      const { data } = await axios.post(
        `${URL_TELAS_ORDENES_CORTE}/salidaCorte`,
        {
          cortes: corte.id,
          fecha,
          observaciones: observaciones || "NA",
          rollos: rollos.map((r) => ({ id: r._id, bies: r.bies })),
        },
        { headers },
      );
      axios
        .post(
          URL_LOGS,
          {
            tipo: "Salida de Tela a Corte",
            detalle: `Corte ${corte.numero}: ${data.rollos} rollos`,
            user: user.id,
          },
          { headers },
        )
        .catch(() => {});
      await Swal.fire({
        icon: "success",
        title: "Salida de tela registrada",
        text: `${data.rollos} rollos · ${fmt(data.totalMetros)} mts / ${fmt(data.totalKilos)} kg`,
      });
      toggle();
    } catch (e) {
      const d = e.response && e.response.data;
      Swal.fire({
        icon: "error",
        title: "No se pudo registrar",
        text: (d && d.message) || "Error al guardar",
      });
    }
    setGuardando(false);
  }

  if (!corte) return null;
  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        <i className="fas fa-scroll" style={{ marginRight: 8 }} />
        Tela del corte {corte.numero}{" "}
        {corte.articulo ? `· ${corte.articulo}` : ""}
      </ModalHeader>
      <ModalBody>
        <div style={{ marginBottom: 12 }}>
          <Button
            size="sm"
            color={modo === "salida" ? "warning" : "light"}
            onClick={() => {
              setModo("salida");
              enfocar();
            }}
          >
            <i className="fas fa-sign-out-alt" /> Dar salida
          </Button>{" "}
          <Button
            size="sm"
            color={modo === "devolucion" ? "info" : "light"}
            onClick={() => {
              setModo("devolucion");
              enfocar();
            }}
          >
            <i className="fas fa-undo-alt" /> Devolver tela
          </Button>
        </div>
        <Row>
          <Col md={3}>
            <Label>Fecha</Label>
            <Input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </Col>
          <Col md={5}>
            {modo === "salida" && (
              <>
                <Label>Observaciones</Label>
                <Input
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                />
              </>
            )}
          </Col>
          <Col md={4}>
            <Label>
              <i className="fas fa-qrcode" style={{ marginRight: 6 }} />
              Escanear rollo
            </Label>
            <Input
              innerRef={scanRef}
              value={scan}
              disabled={buscando}
              placeholder="Escanea el QR del rollo y Enter"
              onChange={(e) => setScan(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  escanear();
                }
              }}
            />
          </Col>
        </Row>
        <br />
        {modo === "devolucion" ? (
          <>
            <h6>Rollos que regresan al almacén</h6>
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>Rollo</th>
                  <th>Tela</th>
                  <th>Color</th>
                  <th>Tiene</th>
                  <th>Regresa</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {devs.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ color: "#94a3b8" }}>
                      Escanea el rollo que regresa, o usa "Devolver" en la lista
                      de tela entregada.
                    </td>
                  </tr>
                ) : (
                  devs.map((r) => (
                    <tr key={r._id}>
                      <td style={{ fontSize: 11 }}>
                        {r.captura ? `#${r.captura} · ` : ""}
                        {r._id}
                      </td>
                      <td>{r.tela}</td>
                      <td>{r.color}</td>
                      <td>
                        {fmt(r.cantidad)} {r.unidad}
                      </td>
                      <td style={{ width: 120 }}>
                        <Input
                          bsSize="sm"
                          type="number"
                          step="any"
                          value={r.regresa}
                          onChange={(e) => {
                            const v = e.target.value;
                            setDevs((prev) =>
                              prev.map((x) =>
                                x._id === r._id ? { ...x, regresa: v } : x,
                              ),
                            );
                          }}
                        />
                        {Number(r.regresa) < Number(r.cantidad) &&
                        Number(r.regresa) > 0 ? (
                          <small style={{ color: "#0ea5e9" }}>
                            parcial: rollo nuevo con QR
                          </small>
                        ) : undefined}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          color="danger"
                          onClick={() =>
                            setDevs((prev) =>
                              prev.filter((x) => x._id !== r._id),
                            )
                          }
                        >
                          <i className="fas fa-times" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </>
        ) : (
          <>
            <h6>Rollos a entregar</h6>
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Rollo</th>
                  <th>Tela</th>
                  <th>Color</th>
                  <th>Cantidad</th>
                  <th>Bies</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rollos.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ color: "#94a3b8" }}>
                      Escanea los rollos que salen para este corte.
                    </td>
                  </tr>
                ) : (
                  rollos.map((r, i) => (
                    <tr key={r._id}>
                      <td>{i + 1}</td>
                      <td style={{ fontSize: 11 }}>
                        {r.captura ? `#${r.captura} · ` : ""}
                        {r._id}
                      </td>
                      <td>{r.tela}</td>
                      <td>{r.color}</td>
                      <td>
                        {fmt(r.cantidad)} {r.unidad}
                      </td>
                      <td style={{ width: 90 }}>
                        <Input
                          bsSize="sm"
                          type="select"
                          value={r.bies}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRollos((prev) =>
                              prev.map((x) =>
                                x._id === r._id ? { ...x, bies: v } : x,
                              ),
                            );
                          }}
                        >
                          <option value="No">No</option>
                          <option value="Si">Si</option>
                        </Input>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          color="danger"
                          onClick={() =>
                            setRollos((prev) =>
                              prev.filter((x) => x._id !== r._id),
                            )
                          }
                        >
                          <i className="fas fa-times" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            <h5>
              TOTALES {fmt(totalUnidad("Kilos"))} kg. /{" "}
              {fmt(totalUnidad("Metros"))} mts. / {rollos.length} rollos
            </h5>
          </>
        )}

        {entregados.length > 0 && (
          <>
            <br />
            <h6>Tela ya entregada a este corte ({entregados.length} rollos)</h6>
            <Table size="sm">
              <thead>
                <tr>
                  <th>Rollo</th>
                  <th>Tela</th>
                  <th>Color</th>
                  <th>Cantidad</th>
                  <th>Bies</th>
                  {modo === "devolucion" ? <th></th> : undefined}
                </tr>
              </thead>
              <tbody>
                {entregados.map((r) => (
                  <tr key={r._id}>
                    <td style={{ fontSize: 11 }}>
                      {r.captura ? `#${r.captura} · ` : ""}
                      {r._id}
                    </td>
                    <td>{r.tela}</td>
                    <td>{r.color}</td>
                    <td>
                      {fmt(r.cantidad)} {r.unidad}
                    </td>
                    <td>{r.bies}</td>
                    {modo === "devolucion" ? (
                      <td>
                        <Button
                          size="sm"
                          color="info"
                          onClick={() => agregarDevolucion(r)}
                          disabled={devs.some((x) => x._id === r._id)}
                        >
                          <i className="fas fa-undo-alt" /> Devolver
                        </Button>
                      </td>
                    ) : undefined}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cerrar
        </Button>
        {modo === "salida" ? (
          <Button
            color="success"
            onClick={guardar}
            disabled={guardando || !rollos.length}
          >
            {guardando ? (
              <Spinner size="sm" />
            ) : (
              <i className="fas fa-save" style={{ marginRight: 6 }} />
            )}{" "}
            Dar salida
          </Button>
        ) : (
          <Button
            color="info"
            onClick={guardarDevolucion}
            disabled={guardando || !devs.length}
          >
            {guardando ? (
              <Spinner size="sm" />
            ) : (
              <i className="fas fa-undo-alt" style={{ marginRight: 6 }} />
            )}{" "}
            Registrar devolución
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default SalidaTelaCorte;
